<template>
  <WdsModal :show="show" :title="$t('New Project')" @cancel="$emit('cancel', $event)">
    <div>
      <div class="tw-mb-6">
        <WdsInput
          v-model="projectName"
          :disabled="loading"
          :label="$t('Name')"
          :placeholder="$t('Give your project a name')"
          :error="errors.projectName.length === 0 ? undefined : errors.projectName.join('\n')"
        />
      </div>
      <div class="tw-mb-6">
        <WdsInputArea
          v-model="projectDescription"
          :disabled="loading"
          :label="$t('Description')"
          :placeholder="$t('Describe your project details')"
          :error="
            errors.projectDescription.length === 0
              ? undefined
              : errors.projectDescription.join('\n')
          "
        />
      </div>
      <div class="tw-mb-6">
        <WdsInputCurrency
          v-model="projectBudget"
          :label="$t('Project budget')"
          :config="rateConfig"
          :disabled="loading"
          :error="errors.projectBudget.length === 0 ? undefined : errors.projectBudget.join('\n')"
        />
      </div>

      <div class="tw-mb-6">
        <label>{{ $t('Owner(s)') }}</label>
        <br />
        <WdsSelect
          v-model="owners"
          multi-select
          can-deselect
          searchable
          :list="companyUsers"
          :placeholder="$t('Select owners')"
          @load-more="loadMoreCompanyUsers"
        />
      </div>
    </div>

    <template #footer>
      <WdsStack justify-space-between>
        <WdsButton :disabled="loading" tertiary @click="$emit('cancelCreate')">
          {{ $t('Cancel') }}
        </WdsButton>
        <WdsStack gap-sm align-center>
          <WdsSpinner v-if="loading" indeterminate />
          <WdsButton primary :disabled="loading" @click="submit">
            {{ $t('Create project') }}
          </WdsButton>
        </WdsStack>
      </WdsStack>
    </template>
  </WdsModal>
</template>

<script lang="ts" setup>
import {
  WdsModal,
  WdsInput,
  WdsInputArea,
  WdsButton,
  WdsSpinner,
  WdsStack,
  WdsInputCurrency,
  WdsSelect,
} from '@worksome/wds-components'
import gql from 'graphql-tag'
import { ref, computed, onBeforeMount } from 'vue'
import { useMutation, useQuery } from '@vue/apollo-composable'
import { $t } from '@/composables/translation'

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  accountId: {
    type: String,
    required: true,
  },
})

defineEmits(['cancel', 'cancelCreate'])

const projectName = ref('')
const projectDescription = ref('')
const projectBudget = ref('')
const loading = ref(false)

type PaginationInfo = {
  hasMorePages: Boolean
  currentPage: Number
}
const companyUsersPaginationInfo = ref<PaginationInfo>({
  hasMorePages: false,
  currentPage: 1,
})
const companyUsersPage = ref(1)

type OwnerOption = {
  label: String
  value: String
}

const owners = ref<OwnerOption[]>([])

const errors = ref({
  projectName: [],
  projectDescription: [],
  projectBudget: [],
})

const companyUsers = ref<OwnerOption[]>([])

const ownerIds = computed(() => owners.value.map((owner) => owner.value))

const rateConfig = computed(() => ({
  currencySymbol: currentAccount.currency ?? '',
  thousandsSeparator: ',',
  decimalSeparator: '.',
  showFractions: true,
}))

onBeforeMount(() => {
  companyUsers.value = companyUsers.value.concat([
    {
      value: window?.currentAccountOwner?.globalId,
      label: window?.currentAccountOwner?.name,
    },
  ])
})

const { onResult } = useQuery(
  gql`
    query accounts($page: Int!) {
      accounts {
        id
        ... on Company {
          teamMembers(page: $page) {
            data {
              id
              name
            }
            paginatorInfo {
              currentPage
              hasMorePages
            }
          }
        }
      }
    }
  `,
  {
    page: companyUsersPage,
  },
  {
    clientId: 'publicApi',
  },
)

onResult((result) => {
  const company = result.data?.accounts
    ?.filter((account) => account.__typename === 'Company' && account.id === props.accountId)
    .pop()

  if (company) {
    const data = company.teamMembers.data.map((teamMember) => ({
      value: teamMember.id,
      label: teamMember.name,
    }))
    companyUsers.value = companyUsers.value.concat(data)
    companyUsersPaginationInfo.value = company.teamMembers.paginatorInfo
  }
})

function loadMoreCompanyUsers() {
  if (companyUsersPaginationInfo.value?.hasMorePages) {
    companyUsersPage.value = companyUsersPaginationInfo.value.currentPage.valueOf() + 1
  }
}

const { mutate: createProjectMutation } = useMutation(
  gql`
    mutation (
      $projectName: String!
      $projectDescription: String
      $projectBudget: Float
      $company: ID!
      $owners: [ID!]
    ) {
      createProject(
        input: {
          name: $projectName
          description: $projectDescription
          internalBudget: $projectBudget
          company: $company
          owners: $owners
        }
      ) {
        id
        name
        description
        company {
          id
        }
        spent
        allocatedBudget
        internalBudget
        creator {
          id
          name
        }
        owners {
          id
        }
      }
    }
  `,
  {
    clientId: 'publicApi',
  },
)

function resetErrors() {
  errors.value = {
    projectName: [],
    projectDescription: [],
    projectBudget: [],
  }
}

async function submit() {
  loading.value = true

  try {
    const res = await createProjectMutation({
      projectName: projectName.value,
      projectDescription: projectDescription.value,
      projectBudget: Number.parseFloat(projectBudget.value),
      company: props.accountId,
      owners: ownerIds.value?.filter((o) => o) ?? [],
    })

    if (res?.data?.createProject?.id) {
      window.location.assign(`/projects/${res.data.createProject.id}`)
    } else {
      loading.value = false
    }
  } catch (err: any) {
    resetErrors()
    err.graphQLErrors.forEach((error) => {
      errors.value.projectName = errors.value.projectName.concat(
        error.extensions.validation['input.name'] ?? error.extensions.validation.projectName ?? [],
      )
      errors.value.projectDescription = errors.value.projectDescription.concat(
        error.extensions.validation['input.description'] ??
          error.extensions.validation.projectDescription ??
          [],
      )
      errors.value.projectBudget = errors.value.projectBudget.concat(
        error.extensions.validation['input.budget'] ??
          error.extensions.validation.projectBudget ??
          [],
      )
    })
    loading.value = false
  }
}
</script>
